import { forwardRef, useId } from 'react';

// ============================================================================
// Input — single input primitive used everywhere a user types.
//
// Anatomy (any piece optional):
//   ┌─────────────────────────────────────┐
//   │ Label                  hint right   │
//   ├─[icon]─────────────────[iconRight]──┤
//   │  input                              │
//   └─[error message]─────────────────────┘
//
// Sizes (sm/md/lg) control padding + font-size. Optional `error` string
// puts the field in error state and shows the message under it; `hint`
// shows a normal description (same slot, lower priority than error).
// ============================================================================

const SIZE = {
  sm: { padX: 12, padY: 7,  font: 12, iconPad: 30, iconSize: 13 },
  md: { padX: 14, padY: 10, font: 13, iconPad: 38, iconSize: 14 },
  lg: { padX: 16, padY: 12, font: 14, iconPad: 42, iconSize: 16 },
};

const Input = forwardRef(function Input({
  label,
  hint,
  hintRight,
  error,
  icon: Icon,
  iconRight: IconRight,
  size = 'md',
  type = 'text',
  fullWidth = true,
  style,
  id: idProp,
  ...rest
}, ref) {
  const reactId = useId();
  const id = idProp || reactId;
  const s = SIZE[size] || SIZE.md;
  const hasError = !!error;

  return (
    <div style={{ width: fullWidth ? '100%' : undefined, ...style }}>
      {(label || hintRight) && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: 6, gap: 8,
        }}>
          {label && (
            <label htmlFor={id} style={{
              fontSize: 10, fontWeight: 600, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {label}
            </label>
          )}
          {hintRight && (
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{hintRight}</span>
          )}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        {Icon && (
          <Icon
            size={s.iconSize}
            color={hasError ? 'var(--red)' : 'var(--text-muted)'}
            style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          />
        )}
        <input
          ref={ref}
          id={id}
          type={type}
          style={{
            width: '100%',
            padding: `${s.padY}px ${IconRight ? s.iconPad : s.padX}px ${s.padY}px ${Icon ? s.iconPad : s.padX}px`,
            borderRadius: 'var(--r-md)',
            border: `1px solid ${hasError ? 'rgba(239,68,68,0.5)' : 'var(--border)'}`,
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            fontSize: s.font, fontFamily: 'inherit',
            outline: 'none', boxSizing: 'border-box',
            transition: 'border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)',
          }}
          {...rest}
        />
        {IconRight && (
          <IconRight
            size={s.iconSize}
            color={hasError ? 'var(--red)' : 'var(--text-muted)'}
            style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
      {(error || hint) && (
        <div style={{
          marginTop: 5, fontSize: 11,
          color: hasError ? 'var(--red)' : 'var(--text-muted)',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          {error || hint}
        </div>
      )}
    </div>
  );
});

export default Input;
